import 'mapbox-gl/dist/mapbox-gl.css';

import { useEffect, useRef, useState } from 'react';

import { getCurrentLanguage, Language } from '@project44/translations-utils';

import { useUser } from '../../hooks/useUser';
import { MAPBOX_GL } from '../../utils/env';
import { mapboxgl } from './lib';
import { setLanguage, setWorldview } from './utils';

mapboxgl.accessToken = MAPBOX_GL;

interface MapProps {
  config?: Omit<mapboxgl.MapboxOptions, 'container'>;
  isChina?: boolean;
  language?: Language;
  onLoad?: (map: mapboxgl.Map) => void;
}

export const MapBox = ({ language, isChina, config, onLoad }: MapProps) => {
  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const { data: user } = useUser({ skipRedirectOn401: true });

  const mapContainer = useRef<null | HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!map || !mapContainer.current || map.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v10',
      renderWorldCopies: true,
      minZoom: 0,
      zoom: 0,
      ...(config ? config : {}),
    });

    map.current.on('load', () => setMapIsLoaded(true));
  });

  useEffect(() => {
    if (!map.current || !mapIsLoaded) return;
    const currentMap = map.current;

    setWorldview(
      currentMap,
      typeof isChina !== 'undefined'
        ? Boolean(isChina)
        : Boolean(user?.customerProperties.MOVEMENT_USE_CHINA_WORLDVIEW),
    );
    setLanguage(currentMap, language || getCurrentLanguage());

    onLoad?.(currentMap);
  }, [mapIsLoaded]);

  return <div style={{ width: '100%', height: '100%' }} ref={mapContainer} />;
};
